/** A tab opened inside a click handler, pointed at its URL once that resolves. */
export interface PendingTab {
  /** Send the reserved tab to `url`, or navigate this tab when the browser refused a new one. */
  navigate: (url: string) => void;
  /** Close the reserved tab, after the URL request fails. */
  close: () => void;
}

/**
 * Reserve a new tab synchronously, while the click's user activation is still
 * live. iOS Safari and other pop-up blockers refuse a `window.open` that runs
 * after an awaited fetch, so the tab opens blank first and receives its URL
 * later. The opener link is cut before any navigation, which the `noopener`
 * feature would otherwise do, because `noopener` makes `window.open` return
 * null. When the browser blocks even the synchronous open, `navigate` falls
 * back to a same-tab navigation.
 */
export function openPendingTab(): PendingTab {
  const tab = window.open("", "_blank");
  if (tab) tab.opener = null;
  return {
    navigate: (url) => {
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    },
    close: () => {
      if (tab && !tab.closed) tab.close();
    },
  };
}
